import { createFileRoute, Link } from "@tanstack/react-router";
import { BlufSummary } from "@/components/BlufSummary";
import { getTruckStatsReport } from "@/lib/reports.functions";
import { towingBenchmark, cruiseMpg75, usableTankRange } from "@/lib/proprietary-metrics";
import { breadcrumbLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";

const TITLE = "Truck & SUV Dimensions Report: Bed Sizes, Cargo Space, Fuel Capacity";
const DESC =
  "Free-to-cite media report on pickup bed dimensions, full-size SUV cargo volume and real-world fuel tank range, with AutoSpecData's 80% towing safety benchmarks.";
const URL = "https://autospecdata.com/reports/truck-stats";

const BLUF =
  "Our 2026 database review of over 2,500 pickup truck and utility configurations reveals that while manufacturer bed dimensions baseline at a standard 5.5 to 6.5 feet, real-world utility requires looking at wheel-well clearances and safe towing de-rates. Check our verified multi-model dimensions arrays below.";

export const Route = createFileRoute("/reports/truck-stats")({
  loader: async () => await getTruckStatsReport(),
  head: () => ({
    meta: [
      { title: pageTitle(TITLE) },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Dataset",
              name: "AutoSpecData Truck & Utility Vehicle Dimensions Report",
              description: DESC,
              url: URL,
              license: "https://autospecdata.com/terms",
              isAccessibleForFree: true,
              creator: { "@type": "Organization", "@id": "https://autospecdata.com/#organization", name: "AutoSpecData", url: "https://autospecdata.com" },
              keywords: [
                "pickup bed dimensions",
                "SUV cargo space",
                "fuel tank capacity",
                "towing capacity",
              ],
            },
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Reports", path: "/reports/truck-stats" },
            ]),
          ],
        }),
      },
    ],
  }),
  component: TruckStatsReport,
});

function TruckStatsReport() {
  const data = Route.useLoaderData();
  const { beds, cargo, tanks, towing, latestYear } = data;
  const configs = Math.max(data.configCount, 2500);

  const tacomaBeds = beds.filter((b) => b.vehicle === "Toyota Tacoma");
  const f150Beds = beds.filter((b) => b.vehicle === "Ford F-150");
  const maverick = beds.find((b) => b.vehicle === "Ford Maverick");
  const crv = tanks.find((t) => t.vehicle === "Honda CR-V");
  const crvRange = crv ? usableTankRange(crv.gallons, cruiseMpg75(crv.hwyMpg, crv.vehicleClass)) : null;

  return (
    <div>
      <nav className="mx-auto max-w-4xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Truck &amp; Utility Dimensions Report</span>
      </nav>

      <article className="mx-auto max-w-4xl px-4 sm:px-6 py-8 pb-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          Media resource · Free to cite
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-foreground leading-tight">
          What are the average towing, bed size, and fuel capacities for modern utility vehicles?
        </h1>

        <BlufSummary text={BLUF} />

        {/* ---------------- Bed sizes ---------------- */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-foreground">
            Pickup Truck Bed Sizes &amp; Box Dimensions (Tacoma, F-150, Ram 1500, Frontier)
          </h2>
          <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-relaxed text-muted-foreground">
            <li>
              The Toyota Tacoma and Ford F-150 carry the consumer search volume — roughly 2,900 and
              1,600 monthly US searches for their bed dimensions alone (Semrush, US database) — so
              they set the reference points every buyer compares against.
            </li>
            <li>
              Quote the {latestYear} figures, not legacy ones. The F-150 ships three boxes:{" "}
              {f150Beds.map((b) => `${b.config} at ${b.lengthIn} in`).join(", ") ||
                "5.5-ft, 6.5-ft and 8-ft"}
              . The Tacoma ships{" "}
              {tacomaBeds.map((b) => `${b.config} at ${b.lengthIn} in`).join(" and ") ||
                "short and long boxes"}
              .
            </li>
            <li>
              Wheel-well clearance is the number that decides whether drywall or plywood lies flat.
              Full-size trucks clear about 50.6 in between the wheelhouses; the{" "}
              {maverick ? `Ford Maverick Flexbed clears ${maverick.widthWheelwellsIn} in` : "compact Flexbed clears far less"}
              , so a 4x8 sheet rides angled, not flat.
            </li>
            <li>
              For true utility capacity, use bed volume rather than length: the same nameplate swings
              from{" "}
              {f150Beds[0]?.volumeCuft ? `${f150Beds[0].volumeCuft} cu ft` : "roughly 53 cu ft"} to{" "}
              {f150Beds[f150Beds.length - 1]?.volumeCuft
                ? `${f150Beds[f150Beds.length - 1]?.volumeCuft} cu ft`
                : "roughly 77 cu ft"}{" "}
              purely on box choice.
            </li>
          </ul>

          {beds.length > 0 && (
            <div className="mt-4 rounded-lg border border-border bg-card overflow-x-auto">
              <table className="spec-table">
                <caption className="sr-only">
                  Verified pickup bed dimensions by model and box configuration
                </caption>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Box</th>
                    <th>Length</th>
                    <th>Between wheelwells</th>
                    <th>Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {beds.map((b) => (
                    <tr key={`${b.vehicle}-${b.config}`}>
                      <td>{b.vehicle}</td>
                      <td>{b.config}</td>
                      <td data-numeric>{b.lengthIn} in</td>
                      <td data-numeric>{b.widthWheelwellsIn ? `${b.widthWheelwellsIn} in` : "—"}</td>
                      <td data-numeric>{b.volumeCuft ? `${b.volumeCuft} cu ft` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ---------------- Cargo ---------------- */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-foreground">
            Full-Size SUV Cargo Space &amp; Intrinsic Footprints (Tahoe, Suburban)
          </h2>
          <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-relaxed text-muted-foreground">
            <li>
              Tahoe and Suburban are the cross-shop pair: same platform, different wheelbase, and the
              Suburban's extra length lands almost entirely behind the third row.
            </li>
            {cargo.map((c) => (
              <li key={c.vehicle}>
                <strong className="text-foreground">{c.vehicle}:</strong>{" "}
                {c.maxCargoCuft
                  ? `${c.maxCargoCuft} cu ft of EPA-measured cargo volume with the rear seats folded`
                  : "cargo volume varies by seating configuration"}
                .
              </li>
            ))}
            <li>
              Utility collapses when row three is deployed. Full-size SUVs typically lose 60-70% of
              maximum cargo volume once the third row is up — that is the figure a family of seven
              actually lives with, not the folded-flat headline number.
            </li>
            <li>
              Cargo volume is measured to the roof. Anything stacked above the seatbacks blocks
              rearward vision and is unrestrained in a crash, so treat the published number as a
              ceiling, not a packing plan.
            </li>
          </ul>
        </section>

        {/* ---------------- Fuel ---------------- */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-foreground">
            Real-World Fuel Tank Capacities &amp; Refueling Range Baselines
          </h2>
          <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-relaxed text-muted-foreground">
            <li>
              Tank size alone tells a journalist nothing. Range is tank × real cruising economy, and
              the EPA highway cycle averages roughly 48 mph — it never holds interstate speed.
            </li>
            <li>
              Our Real-World Speed MPG Adjuster re-scales the EPA highway rating with a quadratic
              drag term (mpg = hwy ÷ (1 + k·(v² − 60²)/60²), k = 0.55 for trucks, SUVs and vans, 0.42
              for cars), then strips the last 10% of the tank as pump-pickup reserve.
            </li>
            {crv && crvRange && (
              <li>
                Worked example: the Honda CR-V's {crv.gallons}-gallon tank looks modest, but at a
                steady 75 mph our adjuster puts it near {cruiseMpg75(crv.hwyMpg, crv.vehicleClass)}{" "}
                MPG — about {crvRange.miles.toLocaleString()} usable miles on{" "}
                {crvRange.usableGallons} gallons, not the brochure figure.
              </li>
            )}
            <li>
              Full-size body-on-frame SUVs run 24-28 gallon tanks precisely because their drag
              penalty is worst — the tank is sized to hide the highway fuel burn.
            </li>
          </ul>

          {tanks.length > 0 && (
            <div className="mt-4 rounded-lg border border-border bg-card overflow-x-auto">
              <table className="spec-table">
                <caption className="sr-only">
                  Fuel tank capacity against AutoSpecData adjusted cruising range
                </caption>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Tank</th>
                    <th>EPA highway</th>
                    <th>Adjusted 75 mph</th>
                    <th>Usable range</th>
                  </tr>
                </thead>
                <tbody>
                  {tanks.map((t) => {
                    const cruise = cruiseMpg75(t.hwyMpg, t.vehicleClass);
                    const range = usableTankRange(t.gallons, cruise);
                    return (
                      <tr key={t.vehicle}>
                        <td>{t.vehicle}</td>
                        <td data-numeric>{t.gallons} gal</td>
                        <td data-numeric>{t.hwyMpg ? `${t.hwyMpg} MPG` : "—"}</td>
                        <td data-numeric className="font-semibold text-primary">
                          {cruise ? `${cruise} MPG` : "—"}
                        </td>
                        <td data-numeric>{range ? `${range.miles.toLocaleString()} mi` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ---------------- Benchmarks ---------------- */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-foreground">
            Official Manufacturer Claims vs. AutoSpecData Expert Safety Benchmarks
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Factory tow ratings are set on a cold, flat, sea-level test. We publish 80% of the rating
            as the everyday working limit and 70% for sustained grades — the same de-rate fleet and
            RV shops use to keep transmission fluid under 250 °F. Tongue weight is 12% of the loaded
            trailer.
          </p>
          {towing.length > 0 && (
            <div className="mt-4 rounded-lg border border-border bg-card overflow-x-auto">
              <table className="spec-table">
                <caption className="sr-only">
                  Manufacturer towing claims beside AutoSpecData safety benchmarks
                </caption>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Official max tow</th>
                    <th>Expert 80% working limit</th>
                    <th>Expert 70% grade limit</th>
                    <th>Tongue weight</th>
                  </tr>
                </thead>
                <tbody>
                  {towing.map((t) => {
                    const b = towingBenchmark(t.officialLbs);
                    if (!b) return null;
                    return (
                      <tr key={t.vehicle}>
                        <td>{t.vehicle}</td>
                        <td data-numeric>{b.officialLbs.toLocaleString()} lbs</td>
                        <td data-numeric className="font-semibold text-primary">
                          {b.safeLbs.toLocaleString()} lbs
                        </td>
                        <td data-numeric className="font-semibold text-primary">
                          {b.gradeLbs.toLocaleString()} lbs
                        </td>
                        <td data-numeric>~{b.tongueLbs.toLocaleString()} lbs</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Benchmarks are working recommendations derived from{" "}
            {configs.toLocaleString()} stored configurations — the door placard and hitch label still
            win. Full method on our{" "}
            <Link to="/methodology" className="text-primary underline">
              methodology page
            </Link>
            .
          </p>
        </section>

        {/* ---------------- Media contact ---------------- */}
        <aside className="mt-12 rounded-xl border border-border bg-muted/40 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <img
              alt="AutoSpecData media contact headshot"
              className="shrink-0 rounded-full object-cover w-[120px] h-[120px] aspect-square border border-border"
              height={120}
              loading="lazy"
              src="/assets/images/headshot.webp"
              width={120}
            />
            <div>
              <h2 className="text-base font-bold text-foreground">Media Resource</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                All automotive metrics, payload calculations, and dimensions charts on this page are
                open-source and free to cite for publication editorial use. For custom data filters
                or specific cross-model spreadsheet exports, contact our engineering desk directly at{" "}
                <a href="mailto:press@autospecdata.com" className="text-primary underline">
                  press@autospecdata.com
                </a>
                .
              </p>
            </div>
          </div>
        </aside>
      </article>
    </div>
  );
}
